/**
 * smartPasteExtractor.js
 * ----------------------
 * When the provider pastes a chunk of text into the notes box (typically
 * copied from MHS Genesis chart review), try to auto-extract structured
 * fields and pre-populate them in the patient demographic fields.
 *
 * Returns null if no demographics could be confidently extracted; otherwise
 * a partial object {age?, gender?, name?, status?, chiefComplaint?}.
 *
 * Feb 16, 2026 — UX improvement #5.
 */

const _genderFromText = (text) => {
  const t = text.toLowerCase();
  if (/\b(female|f\b|woman|she\/her|her,|she is|♀)\b/.test(t)) return 'female';
  if (/\b(male|m\b|man|he\/him|him,|he is|♂)\b/.test(t)) return 'male';
  return null;
};

const _statusFromText = (text) => {
  const t = text.toLowerCase();
  if (/\b(active duty|ad\s|adsm|service member)\b/.test(t)) return 'active_duty';
  if (/\b(veteran|retiree|retired military)\b/.test(t)) return 'veteran';
  if (/\b(dependent|family member|spouse|spouse of)\b/.test(t)) return 'dependent';
  if (/\b(civilian|contractor|civ)\b/.test(t)) return 'civilian';
  return null;
};

const _ageFromText = (text) => {
  // Patterns: "Age: 42", "42-year-old", "42 y/o", "42yo"
  const patterns = [
    /(?:age|age:)\s*[:\-]?\s*(\d{1,3})\b/i,
    /(\d{1,3})\s*[-\s]?year[\s-]?old/i,
    /(\d{1,3})\s*y\/?o\b/i,
    /\b(\d{1,3})\s*yo\b/i,
  ];
  for (const re of patterns) {
    const m = text.match(re);
    if (m) {
      const n = parseInt(m[1], 10);
      if (n >= 1 && n <= 120) return String(n);
    }
  }
  return null;
};

const _ccFromText = (text) => {
  const patterns = [
    /(?:chief complaint|cc)\s*[:\-]\s*(.+?)(?:\.|\n|$)/i,
    /(?:presents? (?:with|for)|here for|c\/o)\s*[:\-]?\s*(.+?)(?:\.|\n|$)/i,
    /(?:reason for visit)\s*[:\-]?\s*(.+?)(?:\.|\n|$)/i,
  ];
  for (const re of patterns) {
    const m = text.match(re);
    if (m && m[1] && m[1].trim().length > 1) {
      return m[1].trim().slice(0, 120);
    }
  }
  return null;
};

const _nameFromText = (text) => {
  const patterns = [
    /(?:patient name|name)\s*[:\-]\s*([A-Z][A-Za-z'\-]+(?:\s+[A-Z][A-Za-z'\-]+){0,2})/,
    /\b(?:mr|mrs|ms|sgt|cpl|spc|pvt|capt|lt|maj|col|gen)\.?\s+([A-Z][A-Za-z'\-]+(?:\s+[A-Z][A-Za-z'\-]+)?)/i,
  ];
  for (const re of patterns) {
    const m = text.match(re);
    if (m && m[1]) return m[1].trim();
  }
  return null;
};

export const extractDemographicsFromPaste = (pasted) => {
  if (!pasted || typeof pasted !== 'string') return null;
  // Only kick in for "substantial" pastes (≥150 chars) that look like chart
  // blob rather than a quick word.
  if (pasted.length < 150) return null;
  const result = {
    age: _ageFromText(pasted),
    gender: _genderFromText(pasted),
    status: _statusFromText(pasted),
    chiefComplaint: _ccFromText(pasted),
    name: _nameFromText(pasted),
  };
  // Drop falsy keys
  const filtered = Object.fromEntries(Object.entries(result).filter(([_, v]) => v));
  return Object.keys(filtered).length > 0 ? filtered : null;
};

export default extractDemographicsFromPaste;
